import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useAuth } from 'react-oidc-context';
import { useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import cloudApi from '../../../app/api/cloudApi';
import { formatFaceScannerDeviceLabel } from '../../../utils/format-face-scanner-model';
import GeneralSettingsTab from './general-settings-tab';
import TimeSettingsTab from './time-settings-tab';
import WiegandSettingsTab from './wiegand-settings-tab';
import IPSettingsTab from './ip-settings-tab';
import WLANSettingsTab from './wlan-settings-tab';
import RelaySettingsTab from './relay-settings-tab';

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index) {
  return {
    id: `settings-tab-${index}`,
    'aria-controls': `settings-tabpanel-${index}`,
  };
}

export default function DeviceSettingsDialog({ open, onClose, site, onDeviceRenamed }) {
  const auth = useAuth();
  const { account } = useSelector((state) => state.app);
  const { enqueueSnackbar } = useSnackbar();

  const [activeTab, setActiveTab] = useState(0);
  const [devices, setDevices] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState('');
  const [loadingDevices, setLoadingDevices] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Load devices when dialog opens
  useEffect(() => {
    if (open && auth.user?.access_token && account?.id && site?.id) {
      loadDevices();
    }
  }, [open, auth.user?.access_token, account?.id, site?.id]);

  const loadDevices = async () => {
    setLoadingDevices(true);
    setError(null);
    try {
      const response = await cloudApi.getFaceScannerConnectedDevices(
        auth.user.access_token,
        site.id,
        account.id
      );

      if (response.data) {
        setDevices(response.data);
        if (response.data.length > 0) {
          setSelectedDevice(response.data[0].deviceID);
        }
      }
    } catch (error) {
      console.error('Error loading devices:', error);
      const errorMessage = error.response?.data || error.message || 'Failed to load devices';
      setError(errorMessage);
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setLoadingDevices(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleDeviceChange = (event) => {
    setSelectedDevice(event.target.value);
  };

  const handleClose = () => {
    setActiveTab(0);
    setSelectedDevice('');
    setError(null);
    onClose();
  };

  const handleDeviceRenamed = (deviceId, newName) => {
    setDevices((prev) =>
      prev.map((d) => (d.deviceID === deviceId ? { ...d, name: newName } : d))
    );
    onDeviceRenamed?.(deviceId, newName);
  };

  const selectedDeviceInfo = devices.find((d) => d.deviceID === selectedDevice) || null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Device Settings</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ mb: 3, mt: 1 }}>
          <FormControl fullWidth disabled={loadingDevices}>
            <InputLabel id="device-select-label">Select Device</InputLabel>
            <Select
              labelId="device-select-label"
              id="device-select"
              value={selectedDevice}
              label="Select Device"
              onChange={handleDeviceChange}
            >
              {devices.map((device) => (
                <MenuItem key={device.deviceID} value={device.deviceID}>
                  {formatFaceScannerDeviceLabel(device)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {loadingDevices ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {selectedDevice && (
              <>
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                  <Tabs value={activeTab} onChange={handleTabChange} aria-label="device settings tabs">
                    <Tab label="General" {...a11yProps(0)} />
                    <Tab label="Time & Timezone" {...a11yProps(1)} />
                    <Tab label="Wiegand Settings" {...a11yProps(2)} />
                    <Tab label="IP Settings" {...a11yProps(3)} />
                    <Tab label="WLAN Settings" {...a11yProps(4)} />
                    <Tab label="Relay Settings" {...a11yProps(5)} />
                  </Tabs>
                </Box>

                <TabPanel value={activeTab} index={0}>
                  <GeneralSettingsTab
                    device={selectedDeviceInfo}
                    deviceId={selectedDevice}
                    siteId={site.id}
                    accountId={account.id}
                    token={auth.user.access_token}
                    onRenamed={handleDeviceRenamed}
                  />
                </TabPanel>

                <TabPanel value={activeTab} index={1}>
                  <TimeSettingsTab
                    deviceId={selectedDevice}
                    siteId={site.id}
                    accountId={account.id}
                    token={auth.user.access_token}
                  />
                </TabPanel>

                <TabPanel value={activeTab} index={2}>
                  <WiegandSettingsTab
                    deviceId={selectedDevice}
                    siteId={site.id}
                    accountId={account.id}
                    token={auth.user.access_token}
                  />
                </TabPanel>

                <TabPanel value={activeTab} index={3}>
                  <IPSettingsTab
                    deviceId={selectedDevice}
                    siteId={site.id}
                    accountId={account.id}
                    token={auth.user.access_token}
                  />
                </TabPanel>

                <TabPanel value={activeTab} index={4}>
                  <WLANSettingsTab
                    deviceId={selectedDevice}
                    siteId={site.id}
                    accountId={account.id}
                    token={auth.user.access_token}
                  />
                </TabPanel>

                <TabPanel value={activeTab} index={5}>
                  <RelaySettingsTab
                    deviceId={selectedDevice}
                    siteId={site.id}
                    accountId={account.id}
                    token={auth.user.access_token}
                  />
                </TabPanel>
              </>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

